import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import type {
  ThematicWritingProfile,
  NewThematicWritingProfile,
  UpdateThematicWritingProfile,
} from '@/types/thematicMaps';

export const useThematicWritingProfiles = (mapId: string | undefined) => {
  const [profiles, setProfiles] = useState<ThematicWritingProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchProfiles = useCallback(async () => {
    if (!user || !mapId) return;

    try {
      setLoading(true);
      const { data, error } = await (supabase as any)
        .from('thematic_writing_profiles')
        .select('*')
        .eq('map_id', mapId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setProfiles((data || []) as ThematicWritingProfile[]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd pobierania profili';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [user?.id, mapId, toast]);

  const createProfile = useCallback(async (
    profileData: Omit<NewThematicWritingProfile, 'user_id' | 'map_id'>
  ): Promise<ThematicWritingProfile | null> => {
    if (!user || !mapId) return null;

    try {
      const { data, error } = await (supabase as any)
        .from('thematic_writing_profiles')
        .insert({
          user_id: user.id,
          map_id: mapId,
          ...profileData,
        })
        .select()
        .single();

      if (error) throw error;

      setProfiles(prev => [...prev, data as ThematicWritingProfile]);
      toast({ title: 'Sukces', description: 'Profil pisarski utworzony' });
      return data as ThematicWritingProfile;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd tworzenia profilu';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return null;
    }
  }, [user, mapId, toast]);

  const updateProfile = useCallback(async (
    id: string,
    updates: UpdateThematicWritingProfile
  ): Promise<boolean> => {
    try {
      const { error } = await (supabase as any)
        .from('thematic_writing_profiles')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      setProfiles(prev => prev.map(p =>
        p.id === id ? { ...p, ...updates } as ThematicWritingProfile : p
      ));
      toast({ title: 'Sukces', description: 'Profil zaktualizowany' });
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd aktualizacji profilu';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return false;
    }
  }, [toast]);

  const deleteProfile = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { error } = await (supabase as any)
        .from('thematic_writing_profiles')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setProfiles(prev => prev.filter(p => p.id !== id));
      toast({ title: 'Sukces', description: 'Profil usunięty' });
      return true;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd usuwania profilu';
      toast({ title: 'Błąd', description: msg, variant: 'destructive' });
      return false;
    }
  }, [toast]);

  useEffect(() => {
    if (user?.id && mapId) {
      fetchProfiles();
    }
  }, [user?.id, mapId, fetchProfiles]);

  const { isLive } = useRealtimeSubscription({
    channel: `writing_profiles_${mapId || 'none'}`,
    table: 'thematic_writing_profiles',
    filter: mapId ? `map_id=eq.${mapId}` : undefined,
    enabled: Boolean(user?.id && mapId),
    onChange: fetchProfiles,
  });

  return {
    profiles,
    loading,
    isLive,
    createProfile,
    updateProfile,
    deleteProfile,
    refreshProfiles: fetchProfiles,
  };
};
